/**
 * Utility class for handling translations
 */
export class TranslationUtil {
  /**
   * Get the translation for a specific language from a list of translations.
   * If the preferred language is not found, it will try the fallback languages in order.
   * 
   * @param translations Array of translation objects
   * @param preferredLanguage The preferred language code (e.g., 'fr', 'en')
   * @param fallbackLanguages Array of fallback language codes to try if preferred language is not found
   * @returns The translation object for the preferred or first available fallback language, or undefined if none found
   */
  static getTranslation<T extends { language?: string }>(
    translations: T[] | undefined,
    preferredLanguage: string,
    fallbackLanguages: string[] = []
  ): T | undefined {
    if (!translations || translations.length === 0) {
      return undefined;
    }

    // Try to find the preferred language
    const preferred = translations.find(t => t.language === preferredLanguage);
    if (preferred) {
      return preferred;
    }

    // Try fallback languages in order
    for (const fallbackLang of fallbackLanguages) {
      const fallback = translations.find(t => t.language === fallbackLang);
      if (fallback) {
        return fallback;
      }
    }

    // Return the first translation if no match found
    return translations[0];
  }

  /**
   * Get available languages from a list of translations
   * 
   * @param translations Array of translation objects
   * @returns Array of unique language codes
   */
  static getAvailableLanguages<T extends { language?: string }>(
    translations: T[] | undefined
  ): string[] {
    if (!translations || translations.length === 0) {
      return [];
    }

    return [...new Set(translations.map(t => t.language).filter(lang => !!lang))] as string[];
  }

  /**
   * Check if a translation exists for a specific language
   * 
   * @param translations Array of translation objects
   * @param language The language code to check
   * @returns true if a translation exists for the language, false otherwise
   */
  static hasTranslation<T extends { language?: string }>(
    translations: T[] | undefined,
    language: string
  ): boolean {
    if (!translations || translations.length === 0) {
      return false;
    }

    return translations.some(t => t.language === language);
  }

  /**
   * Get the translation for a specific language from an object with language keys.
   * If the preferred language is not found, it will try the fallback languages in order.
   *
   * @param translations Object with language codes as keys (e.g., { 'fr': {...}, 'en': {...} })
   * @param preferredLanguage The preferred language code (e.g., 'fr', 'en')
   * @param fallbackLanguages Array of fallback language codes to try if preferred language is not found
   * @returns The translation object for the preferred or first available fallback language, or undefined if none found
   */
  static getTranslationFromObject<T>(
    translations: { [key: string]: T } | undefined,
    preferredLanguage: string,
    fallbackLanguages: string[] = []
  ): T | undefined {
    if (!translations || Object.keys(translations).length === 0) {
      return undefined;
    }

    // Try to find the preferred language
    if (translations[preferredLanguage]) {
      return translations[preferredLanguage];
    }

    // Try fallback languages in order
    for (const fallbackLang of fallbackLanguages) {
      if (translations[fallbackLang]) {
        return translations[fallbackLang];
      }
    }

    // Return the first translation if no match found
    const firstKey = Object.keys(translations)[0];
    return translations[firstKey];
  }
}

